import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { requestPasswordReset } from '../services/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-black via-gray-900 to-amber-950/20 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoMzAwdjMwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')]" style={{backgroundSize: '150px'}}></div>

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        className="w-full max-w-md"
      >
        <Card className="bg-gray-900/95 border-2 border-amber-900/50 shadow-[0_0_30px_rgba(251,191,36,0.2)] backdrop-blur">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-900/30 flex items-center justify-center border border-amber-700/50">
              {sent ? <CheckCircle className="w-6 h-6 text-green-500" /> : <Mail className="w-6 h-6 text-amber-500" />}
            </div>
            <CardTitle className="font-headline text-2xl text-amber-500 tracking-wider">
              {sent ? 'CHECK YOUR INBOX' : 'FORGOT PASSWORD'}
            </CardTitle>
            <CardDescription className="font-typewriter text-gray-400">
              {sent
                ? 'If an account exists with that email, a reset link has been sent.'
                : 'Enter your email and we\'ll send you a reset link.'}
            </CardDescription>
          </CardHeader>

          {!sent ? (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-password-email"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-4 border-t border-amber-900/30 pt-6">
                <Button
                  type="submit"
                  className="w-full bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider"
                  disabled={loading}
                  data-testid="forgot-password-submit"
                >
                  {loading ? 'SENDING...' : 'SEND RESET LINK'}
                </Button>
                <Link to="/login" className="flex items-center gap-2 text-sm text-gray-400 hover:text-amber-500 transition-colors font-typewriter">
                  <ArrowLeft className="w-4 h-4" /> Back to login
                </Link>
              </CardFooter>
            </form>
          ) : (
            <CardFooter className="flex flex-col space-y-4 border-t border-amber-900/30 pt-6">
              <p className="text-sm text-gray-400 font-typewriter text-center">
                Didn't receive it? Check your spam folder or try again.
              </p>
              <Button
                variant="outline"
                className="w-full border-amber-700 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider"
                onClick={() => setSent(false)}
                data-testid="forgot-password-retry"
              >
                TRY AGAIN
              </Button>
              <Link to="/login" className="flex items-center justify-center gap-2 text-sm text-gray-400 hover:text-amber-500 transition-colors font-typewriter">
                <ArrowLeft className="w-4 h-4" /> Back to login
              </Link>
            </CardFooter>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
